import React, { Component } from "react";
import { Modal, Icon, Button, Message } from "semantic-ui-react";
import { Field, Form, Formik } from "formik";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import SecondaryButton from "components/Buttons/SecondaryButton";
import MainButton from "components/Buttons/MainButton";
import * as Yup from "yup";
import { toast } from "react-toastify";
import InputPhone from "components/Form/InputPhone";
import { isValidPhoneNumber, formatPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import Select from "components/Form/Select";
import { connect } from "react-redux";
import { getAllSpecialties } from "services/specialty";
import { searchPostalCodes } from "services/postalCode";
import { getCitiesByPostalCode, resetCities } from "services/city";
import { getStreetsByPostalCode } from "services/street";
import AsyncFilterSelect from "components/Form/AsyncFilterSelect";
import debounce from "lodash.debounce";
import { countriesOptions } from "utils/selectOptions";
import { ErrorAction, errorHandler } from "utils/error";
import { cleanPhoneNumber } from "utils/phone";

const schemaValidation = (detailed) => {
	let validation = {
		last_name: Yup.string().required("Champ requis"),
		first_name: Yup.string().required("Champ requis"),
		code: Yup.string().required("Champ requis").min(9, "Format attendu XXXXXX-XX").max(9, "Format attendu XXXXXX-XX"),
		city: Yup.string().min(2, "Longueur min : 2 caractères").max(250, "Longueur max : 250 caractères").required("Champ requis"),
	};

	if (detailed) {
		validation = {
			...validation,
			email: Yup.string().email("Email incorrect"),
			phone: Yup.string()
				.min(2, "Longueur min : 2 caractères")
				.max(250, "Longueur max : 250 caractères")
				.test("phoneTypeValidation", "Il ne s'agit pas d'un numéro de téléphone mobile ou fixe", (value) => {
					if (formatPhoneNumber(value) !== "") {
						const type = parsePhoneNumberFromString(value).getType();
						return type === "MOBILE" || type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
					}
				})
				.test("phoneValidation", "Le format du téléphone mobile est inconu", (value) => isValidPhoneNumber(cleanPhoneNumber(value)))
				.required("Champs requis"),
			fax: Yup.string()
				.min(2, "Longueur min : 2 caractères")
				.max(250, "Longueur max : 250 caractères")
				.test("phoneTypeValidation", "Il ne s'agit pas d'un fax", (value) => {
					if (formatPhoneNumber(value) !== "") {
						const type = parsePhoneNumberFromString(value).getType();
						return type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
					}
					return true;
				})
				.test("phoneValidation", "Le format du fax est inconu", (value) => {
					value = cleanPhoneNumber(value);
					return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
				}),
			street: Yup.string().min(2, "Longueur min : 2 caractères").max(250, "Longueur max : 250 caractères").required("Champ requis"),
			complement: Yup.string().min(2, "Longueur min : 2 caractères").max(250, "Longueur max : 250 caractères"),
			number: Yup.string().max(250, "Longueur max : 250 caractères"),
			postal_code: Yup.string().min(2, "Longueur min : 2 caractères").max(250, "Longueur max : 250 caractères").required("Champ requis"),
			country: Yup.string().min(2, "Longueur min : 2 caractères").max(250, "Longueur max : 250 caractères").required("Champ requis"),
			specialty: Yup.object().shape({ id: Yup.number().nullable() }).required(),
		};
	}

	return Yup.object().shape(validation);
};

class EditDoctorModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			showDetailedForm: false,
			postalCodeLoading: false,
			showDoctorCodeWarning: false,
		};
	}

	componentDidUpdate(_prevProps, prevState, _snapshot) {
		if (prevState.showDetailedForm !== this.state.showDetailedForm && this.state.showDetailedForm) this.props.getAllSpecialties();
	}

	handleOnSubmit = (values, resetForm, setSubmitting) => {
		this.props
			.submit(values)
			.then(() => {
				resetForm();
				this.props.close();
				toast.success("Docteur sauvegardé");
			})
			.catch((err) => {
				this.props.close();
				errorHandler("Docteur", this.props.doctor?.id ? ErrorAction.UPDATE : ErrorAction.CREATE, err);
			})
			.then(() => setSubmitting(false));
	};

	handleOnClose = (resetForm) => {
		resetForm();
		this.props.close();
	};

	filterPostalCodes = debounce((searchTerm, country, callback) => {
		this.props
			.searchPostalCodes({ code: searchTerm, country: country }, 10, 0)
			.then(() => {
				const postCodes = this.props.postalCodes.list.map((postalCode) => {
					return postalCode.cities.map((city) => {
						const defaultValue = {
							value: postalCode.code,
							label: `${postalCode.code} - ${city.name}`,
							country: postalCode.country_iso,
							city: city,
							id: postalCode.id,
						};
						if (postalCode.streets.length > 0) {
							return postalCode.streets.map((street) => {
								if (street.displayWithPostalCode)
									return {
										value: postalCode.code,
										label: `${street.name} - ${postalCode.code} - ${city.name}`,
										country: postalCode.country_iso,
										city: city,
										street: street,
										id: postalCode.id,
									};
								return defaultValue;
							});
						}
						return defaultValue;
					});
				});
				const mergedPostCodes = [].concat.apply([], [].concat.apply([], postCodes));
				this.setState({ postalCodeLoading: false });
				callback(mergedPostCodes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)));
			})
			.catch((error) => {
				this.setState({ postalCodeLoading: false });
				callback(error);
			});
	}, 500);

	checkCodeMod97 = (code) => {
		if (code && code.length === 9) {
			const part = code.substring(0, 6);
			const check = code.substring(7, 9);
			const mod97 = part % 97;
			if (mod97 !== parseInt(check)) this.setState({ showDoctorCodeWarning: true });
			else this.setState({ showDoctorCodeWarning: false });
		} else if (this.state.showDoctorCodeWarning) {
			this.setState({ showDoctorCodeWarning: false });
		}
	};

	render() {
		const { close, specialties, cities, streets, doctor } = this.props;
		const { showDetailedForm, showDoctorCodeWarning } = this.state;

		const specialtiesOptions = specialties.list.map((specialty) => {
			return {
				value: specialty.id,
				label: specialty.name,
			};
		});

		return (
			<Formik
				initialValues={{
					id: doctor?.id ?? null,
					last_name: doctor?.last_name ?? "",
					first_name: doctor?.first_name ?? "",
					code: doctor?.code ?? "",
					specialty: {
						id: doctor?.specialty?.id ?? specialties.list.find((spe) => spe?.name?.toLowerCase() === doctor?.speciality?.toLowerCase())?.id ?? null,
					},
					email: doctor?.email ?? "",
					phone: doctor?.phone ? cleanPhoneNumber(doctor?.phone) : "",
					fax: doctor?.fax ?? "",
					number: doctor?.number ?? doctor?.address?.number ?? "",
					street: doctor?.street ?? doctor?.address?.street ?? "",
					complement: doctor?.complement ?? doctor?.address?.complement ?? "",
					postal_code: doctor?.postal_code ?? doctor?.address?.postal_code ?? "",
					city: doctor?.city ?? doctor?.address?.city ?? "",
					country: doctor?.country ?? doctor?.address?.country ?? "",
				}}
				validationSchema={schemaValidation(showDetailedForm)}
				onSubmit={(values, { setSubmitting, resetForm }) => {
					this.handleOnSubmit(values, setSubmitting, resetForm);
				}}
			>
				{({ isSubmitting, handleSubmit, resetForm, values, setFieldValue, handleChange }) => {
					return (
						<Modal open onClose={close} size={"tiny"} centered={false} closeOnDimmerClick={false}>
							<Modal.Header>{doctor?.id ? "Modifier un docteur" : "Ajouter un docteur"}</Modal.Header>
							<Modal.Content>
								{showDoctorCodeWarning && <Message warning header="Attention" content="Il semblerait que ce code médecin ne soit pas bon" />}
								<Form>
									{showDetailedForm && (
										<Field
											component={Select}
											name="specialty.id"
											customlabel="Spécialité"
											placeholder="Sélectionner une spécialité"
											defaultValue={specialtiesOptions ? specialtiesOptions[0] : null}
											options={specialtiesOptions}
										/>
									)}

									<Field
										type="text"
										name="code"
										customlabel="Code"
										placeholder=""
										component={CustomInputTextComponent}
										required
										onChange={(e) => {
											if (e.target.value.length === 6 && !e.target.value.includes("-")) e.target.value += "-";
											this.checkCodeMod97(e.target.value);
											handleChange(e);
										}}
									/>

									<Field type="text" name="last_name" customlabel="Nom" placeholder="" component={CustomInputTextComponent} required forceUpperCase />

									<Field
										type="text"
										name="first_name"
										customlabel="Prénom(s)"
										placeholder=""
										component={CustomInputTextComponent}
										required
										forceCapitalize
									/>

									{!showDetailedForm && (
										<Field type="text" name="city" customlabel="Ville" placeholder="" component={CustomInputTextComponent} required />
									)}

									{showDetailedForm && (
										<>
											<Field type="text" name="email" placeholder="" customlabel="Adresse e-mail" component={CustomInputTextComponent} required />

											<Field component={InputPhone} name="phone" id="phone" customlabel="Téléphone" required />

											<Field component={InputPhone} name="fax" id="fax" customlabel="Fax" />

											<Field component={Select} name="country" customlabel="Pays" options={countriesOptions()} isCreatable required />

											<Field
												defaultValue={{
													value: values.postal_code,
													label: values.postal_code,
												}}
												component={AsyncFilterSelect}
												name="postal_code"
												customlabel="Code Postal"
												promiseOptions={(code, callback) => {
													this.setState({ postalCodeLoading: true });
													this.filterPostalCodes(code, values.country, callback);
												}}
												isLoading={this.state.postalCodeLoading}
												customOnChange={async (option) => {
													if (values.country === "") {
														setFieldValue("country", option.country);
													}
													setFieldValue("postal_code", option.value);
													this.props.getCitiesByPostalCode(option.id).then(() => {
														setFieldValue("city", option.city.name);
													});
													this.props.getStreetsByPostalCode(option.id).then(() => {
														setFieldValue("street", option.street?.name || "");
													});
												}}
												isCreatable
												onCreateClick={(val) => {
													setFieldValue("postal_code", val);
													resetCities();
												}}
												required
											/>

											<Field
												component={Select}
												name="city"
												customlabel="Ville"
												options={cities.list.map((city) => {
													return {
														value: city.name,
														label: city.name,
													};
												})}
												isCreatable
												required
											/>

											<Field type="text" name="number" customlabel="N°" placeholder="" component={CustomInputTextComponent} />

											<Field
												component={Select}
												name="street"
												customlabel="Rue"
												options={
													values.home_postal_code
														? streets.list.map((street) => {
																return {
																	value: street.name,
																	label: street.name,
																};
														  })
														: []
												}
												isCreatable
												required
											/>

											<Field
												type="text"
												name="complement"
												customlabel="Complément"
												placeholder="Batiment, étage, porte"
												component={CustomInputTextComponent}
											/>
										</>
									)}
								</Form>
							</Modal.Content>
							<Modal.Actions>
								<Button
									content={showDetailedForm ? "Moins de détails" : "Plus de détails"}
									icon={showDetailedForm ? "minus" : "add"}
									labelPosition="left"
									onClick={() => this.setState({ showDetailedForm: !showDetailedForm })}
								/>
								<SecondaryButton
									onClick={() => {
										this.handleOnClose(resetForm);
									}}
								>
									Annuler
								</SecondaryButton>
								<MainButton text={<Icon name="save" />} type="submit" isSubmitting={isSubmitting} onClick={handleSubmit} disabled={isSubmitting}>
									Enregistrer
								</MainButton>
							</Modal.Actions>
						</Modal>
					);
				}}
			</Formik>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		specialties: state.specialties,
		postalCodes: state.postalCodes,
		cities: state.cities,
		streets: state.streets,
	};
};

const mapDispatchToProps = {
	getAllSpecialties,
	searchPostalCodes,
	getCitiesByPostalCode,
	getStreetsByPostalCode,
	resetCities,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditDoctorModal);
